<script setup lang="ts">
import { useOverlayStore } from "@/shop/store"
import { sleep, useHttpClient } from "@/vf"
import type { AxiosError } from "axios"
import { ref, toRaw } from "vue"
import { useRouter } from "vue-router"
import { LoginOverlay } from "."
import { useShopAppConfig } from "@/shop/composables"
import { presetImages } from "@/common/assets/preset-images/preset-images"
import { useCustomDeliveryAddressStore } from "@/shop/store/useCustomDeliveryAddressStore"

defineExpose({
    name: "LoginOverlay",
})

const overlayStore = useOverlayStore()
const customDeliveryAddressStore = useCustomDeliveryAddressStore()

// for description of what these events mean, see useAuthentication composable

/**
 * Authentication successful, user is now logged in.
 */
const onAuthenticated = () => {
    customDeliveryAddressStore.reset()
    overlayStore.props?.onAuthenticated?.()
}

/**
 * Overlay was closed without authentication.
 */
const onClosed = () => {
    const fn = overlayStore.props?.onClosed ?? (() => router.push({ name: "home" }))

    fn()
}

/**
 * User has cancelled the login process by navigating away from the login overlay (e.g. by going to the registration
 * page).
 */
const onCancelled = () => {
    const fn = overlayStore.props?.onCancelled

    fn?.()
}

async function login(unblock: boolean): Promise<void> {
    if (!email.value || !password.value) {
        error.value = "Bitte gib deine E-Mailadresse und dein Passwort ein."
        await sleep(2000)
        error.value = undefined
        return
    }

    try {
        if (unblock) {
            showDialog.value = "afterUnblock"
        }

        error.value = undefined
        await http.post("login", {
            username: email.value,
            password: password.value,
            _remember_me: rememberMe.value,
            unblock: unblock ? 1 : 0,
        })

        const appConfig = await refreshAppConfig()
        if (appConfig.value.customAfterLoginDialog) {
            await showCustomAfterLoginDialog(
                appConfig.value.customAfterLoginDialog.text,
                appConfig.value.customAfterLoginDialog.presetImageIndex,
            )
        }

        // check if this overlay is still the active one,
        // because app config subscribers can open other overlays (i.e. user-info)
        if (toRaw(overlayStore.component) === LoginOverlay) {
            overlayStore.close()
        }

        onAuthenticated()
    } catch (exc) {
        console.error(exc)
        const e = exc as AxiosError<{ error: string }>
        const msg = e.response?.data.error

        // If account is blocked due to inactivity, allow user to self-unblock and retry login
        if (msg && msg.includes("Inaktivität gesperrt")) {
            showDialog.value = "unblock"
            return
        }

        error.value = msg ?? "Unbekannter Fehler"
    }
}

/**
 * Close the overlay without any further action.
 */
function close() {
    overlayStore.close()
    onClosed()
}

/**
 * Cancel the authentication process and leave to a different route.
 */
function cancelAndNavigate(route: string): void {
    overlayStore.close()
    onCancelled()
    router.push({ name: route })
}

/*─────────────────────────────────────┐
│   composables                        │
└─────────────────────────────────────*/
const { refreshAppConfig } = useShopAppConfig()
const http = useHttpClient()
const router = useRouter()
/*─────────────────────────────────────┐
│   refs                               │
└─────────────────────────────────────*/
const password = ref<string>()
const email = ref<string>()
const error = ref<string>()
const rememberMe = ref<boolean>()
const showDialog = ref<"unblock" | "afterUnblock" | "afterLogin" | "login" | "confirmDelete" | "deletionSuccess">(
    "login",
)
const isDeleting = ref<boolean>(false)
const deleteError = ref<string>()
/*─────────────────────────────────────┐
│  custom after login dialog           │
└─────────────────────────────────────*/
const afterLoginDialogText = ref<string>()
const loginDialogImageIndex = ref<number>()

let dismissAfterLoginDialog: () => void

function showCustomAfterLoginDialog(text: string, imageIndex?: number): Promise<void> {
    showDialog.value = "afterLogin"
    afterLoginDialogText.value = text
    loginDialogImageIndex.value = imageIndex ?? undefined

    return new Promise<void>(resolve => {
        dismissAfterLoginDialog = resolve
    })
}
/*─────────────────────────────────────┐
│  delete inactive account             │
└─────────────────────────────────────*/
function confirmDeleteAccount(): void {
    showDialog.value = "confirmDelete"
}

async function deleteInactiveAccount(): Promise<void> {
    if (!email.value || !password.value) {
        deleteError.value = "Bitte gib deine E-Mailadresse und dein Passwort ein."
        return
    }

    isDeleting.value = true
    deleteError.value = undefined

    try {
        await http.post("my-account/delete-inactive-account", {
            username: email.value,
            password: password.value,
        })

        // Show success dialog
        showDialog.value = "deletionSuccess"
    } catch (exc) {
        console.error(exc)
        const e = exc as AxiosError<{ error: string }>
        deleteError.value = e.response?.data.error ?? "Unbekannter Fehler"
    } finally {
        isDeleting.value = false
    }
}

function cancelDelete(): void {
    showDialog.value = "unblock"
    deleteError.value = undefined
}
</script>

<template>
    <div v-if="showDialog === 'unblock'" class="login">
        <div class="login-yin login-full text-left">
            <div class="mb-5">
                <p>
                    Weil du dein Kundenkonto schon längere Zeit nicht aktiv genutzt hast, haben wir es vorübergehend
                    deaktiviert. Um es weiterhin zu nutzen, kannst du es entsperren und innerhalb von 30 Tagen eine
                    Bestellung vornehmen.
                </p>
                <p>
                    Du musst dein Kundenkonto nicht sofort entsperren – es passiert nichts, wenn du aktuell nichts
                    unternimmst. Die 30-Tage-Frist beginnt erst, sobald du dein Konto entsperrst.
                </p>
                <p>
                    Solltest du nach dem Entsperren innerhalb von 30 Tagen keine Bestellung vornehmen, wird dein Konto
                    dauerhaft gesperrt.
                </p>
            </div>
            <div class="row">
                <div class="col-12 col-md-6 mb-3 mb-md-0">
                    <button type="button" class="btn btn-primary-filled btn-block" @click="login(true)">
                        Jetzt entsperren
                    </button>
                </div>
                <div class="col-12 col-md-6 mb-3 mb-md-0">
                    <button type="button" class="btn btn-primary btn-block" @click="close()">
                        Ich komme später wieder!
                    </button>
                </div>
                <div class="col-12 text-center mt-3">
                    <button type="button" class="btn btn-link text-danger p-0" @click="confirmDeleteAccount()">
                        Account dauerhaft löschen
                    </button>
                </div>
                <div class="col-12 text-center mt-3">
                    <RouterLink
                        class="btn btn-link text-primary p-0"
                        :to="{ name: 'legal-inactivity-info' }"
                        @click="close()"
                    >
                        Warum wir inaktive Konten deaktivieren
                    </RouterLink>
                </div>
            </div>
        </div>
    </div>
    <div v-else-if="showDialog === 'confirmDelete'" class="login">
        <div class="login-yin login-full text-left">
            <div class="mb-5">
                <h4 class="mb-4">Account wirklich löschen?</h4>
                <p>
                    Hinweis: Dein Kundenkonto wird 6 Monate nach deiner letzten Bestellung endgültig gelöscht. Dein
                    Kundenkonto bleibt bis dahin in unserem System, da wir per Gesetz dazu verpflichtet sind, dich im
                    Falle des Bekanntwerdens von Arzneimittelrisiken und Produktmängeln über diese zu informieren.
                    Solltest du noch nie eine Bestellung bei uns gemacht haben, wird dein Kundenkonto innerhalb von 24
                    Stunden unwiderruflich gelöscht.
                </p>
                <p>Bist du sicher, dass du fortfahren möchtest?</p>
            </div>
            <div v-if="deleteError" class="alert alert-danger mb-4">{{ deleteError }}</div>
            <div class="row">
                <div class="col-12 col-md-6 mb-3 mb-md-0">
                    <button
                        type="button"
                        class="btn btn-danger btn-block"
                        :disabled="isDeleting"
                        @click="deleteInactiveAccount()"
                    >
                        <i v-if="isDeleting" class="fa fa-fw fa-circle-notch fa-spin"></i>
                        <span v-else>Ja, Account löschen</span>
                    </button>
                </div>
                <div class="col-12 col-md-6">
                    <button
                        type="button"
                        class="btn btn-primary btn-block"
                        :disabled="isDeleting"
                        @click="cancelDelete()"
                    >
                        Abbrechen
                    </button>
                </div>
            </div>
        </div>
    </div>
    <div v-else-if="showDialog === 'afterUnblock'" class="login">
        <div class="login-yin login-full text-center py-5">
            <i class="fa fa-fw fa-circle-notch fa-spin fa-2x"></i>
            <div class="mt-5">Du wirst entsperrt. Bitte habe einen Moment Geduld.</div>
        </div>
    </div>
    <div v-else-if="showDialog === 'deletionSuccess'" class="login">
        <div class="login-yin login-full text-center">
            <div class="mb-4">
                <h4 class="mb-4">Account erfolgreich zum Löschen vorgemerkt.</h4>
                <i class="fa fa-check-circle fa-3x text-success"></i>
            </div>
            <div class="d-flex justify-content-start">
                <button type="button" class="btn btn-primary" @click="close()">Schließen</button>
            </div>
        </div>
    </div>
    <div v-else-if="showDialog === 'afterLogin'" class="login">
        <div class="login-yin login-full text-left">
            <div class="row mb-5">
                <div
                    class="col-12"
                    :class="{ 'col-md-8': loginDialogImageIndex !== undefined }"
                    v-html="afterLoginDialogText"
                ></div>
                <div v-if="loginDialogImageIndex !== undefined" class="d-flex col-12 col-md-4">
                    <img
                        style="max-height: 500px"
                        class="img-fluid mx-auto my-auto d-block"
                        :src="presetImages[loginDialogImageIndex]"
                        alt=""
                    />
                </div>
            </div>

            <button type="button" class="btn btn-primary" @click="dismissAfterLoginDialog()">Schließen</button>
        </div>
    </div>
    <div v-else-if="showDialog === 'login'" class="login">
        <button class="overlay-close" @click="close()"><i class="fa fa-times"></i></button>

        <div class="login-yin">
            <div class="login-heading">Login</div>
            <form @submit.prevent="login(false)">
                <div v-if="error" class="alert alert-danger">{{ error }}</div>
                <div class="login-input">
                    <i class="fas fa-fw fa-user"></i>
                    <label for="login_email" class="sr-only">E-Mailadresse</label>
                    <input
                        id="login_email"
                        v-model="email"
                        type="email"
                        placeholder="E-Mailadresse"
                        class="form-control"
                    />
                </div>

                <div class="login-input">
                    <i class="fas fa-fw fa-lock"></i>
                    <label for="login_password" class="sr-only">Passwort</label>
                    <input
                        id="login_password"
                        v-model="password"
                        type="password"
                        placeholder="Passwort"
                        class="form-control"
                    />
                </div>
                <!-- problems with json_login -->
                <!--                <div>-->
                <!--                    <label>-->
                <!--                        <input type="checkbox" checked.bind="rememberMe">-->
                <!--                        Remember Me-->
                <!--                    </label>-->
                <!--                </div>-->

                <button class="btn btn-primary my-4" type="submit">Sicherer Login</button>
            </form>
            <div>
                <RouterLink :to="{ name: 'lost-password' }" @click="cancelAndNavigate('lost-password')">
                    Passwort vergessen?
                </RouterLink>
            </div>
        </div>
        <div class="login-yang">
            <div class="login-heading">Registrierung</div>

            <p>
                Du bist noch nicht Mitglied der CANN24-Community? Dann registriere dich jetzt, um alle unsere Leistungen
                in Anspruch nehmen zu können.
            </p>

            <RouterLink
                class="btn btn-primary my-4"
                :to="{ name: 'registration' }"
                @click="cancelAndNavigate('registration')"
            >
                Jetzt registrieren
            </RouterLink>

            <div>
                <RouterLink :to="{ name: 'legal-toc' }" @click="cancelAndNavigate('legal-toc')">Unsere AGB</RouterLink>
            </div>
        </div>
    </div>
</template>
